/// <reference types="vite/client" />
import {
	createRootRouteWithContext,
	HeadContent,
	Scripts,
} from '@tanstack/react-router';
import type * as React from 'react';
import { DefaultCatchBoundary } from '@/components/DefaultCatchBoundary';
import LinearProgress from '@/components/linear-progress';
import { NotFound } from '@/components/NotFound';
import { RouteChangeTracker } from '@/lib/analytics/RouteChangeTracker';
import { LoginProvider } from '@/lib/login';
import { onReady } from '@/lib/pwa';
import appCss from '@/styles/app.css?url';
import type { AppContext } from '@/utils/headers';
import { seo } from '@/utils/seo';

export const Route = createRootRouteWithContext<AppContext>()({
	head: () => ({
		meta: [
			{
				charSet: 'utf-8',
			},
			{
				name: 'viewport',
				content: 'width=device-width, initial-scale=1',
			},
			{
				name: 'theme-color',
				content: '#ffffff',
			},
			...seo({
				title: 'Book Airport Cabs on ixigo | Quick, Safe & Affordable Ride',
				description:
					'Find and book trusted airport cabs and taxi services from and to the airport. Quick booking, affordable fares & safe travel.',
				image:
					'https://images.ixigo.com/image/upload/cab/92728a5435b0cdaad985a25c4b1f8af8-qgglh.png',
			}),
			{
				name: 'robots',
				content: 'noindex, nofollow',
			},
		],
		links: [
			{ rel: 'stylesheet', href: appCss },
			{
				rel: 'icon',
				href: 'https://images.ixigo.com/image/upload/new/3c5218eaccbc128a6c0ec461a627daf8-mbsmf.png',
			},
		],
		scripts: [],
	}),
	errorComponent: (props) => {
		return (
			<RootDocument>
				<DefaultCatchBoundary {...props} />
			</RootDocument>
		);
	},
	notFoundComponent: () => <NotFound />,
	shellComponent: RootComponent,
	ssr: true,
	loader: async ({ context }) => {
		return context._theme;
	},
});

function RootComponent({ children }: { children: React.ReactNode }) {
	return <RootDocument>{children}</RootDocument>;
}

function RootDocument({ children }: { children: React.ReactNode }) {
	const { style: themeCss, script: themeScript } = Route.useLoaderData() || {};
	return (
		<html lang="en" suppressHydrationWarning>
			<head>
				{themeScript ? (
					<script
						// biome-ignore lint/security/noDangerouslySetInnerHtml: internal inline script for theme
						dangerouslySetInnerHTML={{ __html: themeScript }}
					/>
				) : null}
				<script
					// biome-ignore lint/security/noDangerouslySetInnerHtml: internal script to handle pwa ready
					dangerouslySetInnerHTML={{ __html: `(${onReady.toString()})();` }}
				/>
				<HeadContent />
				{themeCss ? (
					<style id="app-theme" type="text/css">
						{themeCss}
					</style>
				) : null}
			</head>
			<body className="bg-primary text-primary font-primary">
				<RouteChangeTracker />
				<LoginProvider>{children}</LoginProvider>
				<LinearProgress />
				<Scripts />
			</body>
		</html>
	);
}
